import { useCallback, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Icon } from '@iconify/react'
import styles from './Tabs.module.css'

export interface TabLinkProps {
  children: React.ReactNode
  /** Rota para navegação */
  to: string
  /** Identificador único da aba */
  value: string
  /** Ícone opcional (Iconify) */
  icon?: string
  /** Se está desabilitada */
  disabled?: boolean
  /** Badge opcional */
  badge?: string | number
  /** Classe CSS adicional */
  className?: string
  /** Se é a primeira aba (rota base). Se true, só está ativa quando a rota é exatamente igual */
  isBaseRoute?: boolean
  /** Seleção explícita para abas que compartilham a rota e variam pelo hash. */
  active?: boolean
}

/**
 * Botão de aba que navega usando Link do react-router-dom.
 * Usado quando as abas precisam ser rotas separadas.
 */
export function TabLink({
  children,
  to,
  value,
  icon,
  disabled = false,
  badge,
  className,
  isBaseRoute = false,
  active,
}: TabLinkProps) {
  const location = useLocation()
  const linkRef = useRef<HTMLAnchorElement>(null)

  // Verifica se a rota atual corresponde a esta aba
  // Se é a rota base (primeira aba), só está ativa quando a rota é exatamente igual
  // Se não é a rota base, está ativa quando é exatamente igual OU quando é uma subpágina
  const tabPathname = to.split(/[?#]/, 1)[0]
  const isExactMatch = location.pathname === tabPathname
  const isSubPage = location.pathname.startsWith(`${tabPathname}/`)

  const isActive =
    active ??
    (isBaseRoute
      ? isExactMatch // Rota base só ativa quando é exatamente igual
      : isExactMatch || isSubPage) // Outras rotas ativas quando são exatas ou subpáginas

  // Navegação por teclado
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return

      const tabs = linkRef.current
        ?.closest('[role="tablist"]')
        ?.querySelectorAll('[role="tab"]')
      if (!tabs) return

      const tabsArray = Array.from(tabs) as HTMLElement[]
      const currentIndex = tabsArray.findIndex((tab) => tab === linkRef.current)

      let nextIndex: number | null = null

      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = currentIndex > 0 ? currentIndex - 1 : tabsArray.length - 1
          break
        case 'ArrowRight':
          nextIndex = currentIndex < tabsArray.length - 1 ? currentIndex + 1 : 0
          break
        case 'Home':
          nextIndex = 0
          break
        case 'End':
          nextIndex = tabsArray.length - 1
          break
        default:
          return
      }

      e.preventDefault()
      const nextTab = tabsArray[nextIndex]
      nextTab?.focus()
      if (nextTab instanceof HTMLAnchorElement) {
        nextTab.click()
      }
    },
    [disabled]
  )

  const tabId = `tab-${value}`

  if (disabled) {
    return (
      <span
        role="tab"
        aria-selected={false}
        aria-disabled={true}
        tabIndex={-1}
        data-tab-value={value}
        className={[styles.tab, styles.tabDisabled, className].filter(Boolean).join(' ')}
      >
        {icon && <Icon icon={icon} className={styles.tabIcon} />}
        <span className={styles.tabLabel}>{children}</span>
        {badge !== undefined && <span className={styles.tabBadge}>{badge}</span>}
      </span>
    )
  }

  return (
    <Link
      ref={linkRef}
      to={to}
      id={tabId}
      role="tab"
      aria-selected={isActive}
      aria-disabled={disabled}
      tabIndex={isActive ? 0 : -1}
      data-tab-value={value}
      onKeyDown={handleKeyDown}
      className={[styles.tab, isActive && styles.tabActive, className]
        .filter(Boolean)
        .join(' ')}
    >
      {icon && <Icon icon={icon} className={styles.tabIcon} />}
      <span className={styles.tabLabel}>{children}</span>
      {badge !== undefined && <span className={styles.tabBadge}>{badge}</span>}
    </Link>
  )
}
